// Quote Builder — Kit-Q AI assistant.
//
// Slide-out chat panel; sends user message + quote state + recent history
// to POST /api/qb-assistant (kitchenos-api/modules/qb_assistant.js:718) and
// applies the returned `actions[]` to the quote store.
//
// Mirrors the legacy `injectAiPanel` + `applyAiActions` flow at
// kos-qb-v3.js:5987-6212.

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";
import type { QuoteStore } from "./state";
import type { PageContext } from "../../shell/types";
import type { KitQAction, KitQResponse, QuoteItem } from "./types";
import { escapeHtml, newId } from "./formatters";
import { calcRetailGbp, calcRetailPct, recalcItem } from "./pricing";
import { pushItemToZone } from "./simple-pickers";

type AddItemsAction = Extract<KitQAction, { tool: "add_items" }>;
type ItemPartial = NonNullable<AddItemsAction["items"]>[number];

interface ChatMsg {
  role: "user" | "assistant" | "system";
  content: string;
}

export interface KitQHandle {
  open: () => void;
  close: () => void;
  destroy: () => void;
}

/**
 * Mount the Kit-Q panel into `host`. Returns a handle to open/close it
 * imperatively from the header button.
 */
export function mountKitQ(
  host: HTMLElement,
  store: QuoteStore,
  ctx: PageContext,
): KitQHandle {
  const history: ChatMsg[] = [];
  let abortController: AbortController | null = null;
  let thinking = false;

  host.innerHTML = `
    <div class="qb-kitq-backdrop" data-role="kitq-backdrop"></div>
    <aside class="qb-kitq" data-role="kitq" aria-label="Kit-Q AI assistant">
      <header class="qb-kitq__head">
        <div class="qb-kitq__avatar" aria-hidden="true">Q</div>
        <div class="qb-kitq__title">
          <div class="qb-kitq__name">Kit-Q</div>
          <div class="qb-kitq__sub">Quote Builder AI · scoped to your catalogues</div>
        </div>
        <button type="button" class="qb-kitq__close" data-role="close" aria-label="Close Kit-Q">${iconMarkup("close", { size: "sm" })}</button>
      </header>
      <div class="qb-kitq__msgs" data-role="msgs">
        <div class="qb-kitq__msg qb-kitq__msg--assistant">
          Hi — I'm Kit-Q. Tell me what you need and I'll build it into the quote.
          Try: <em>"Add 3× 600mm base units, 2 LH and 1 RH"</em> or
          <em>"What Cartmel colours are available?"</em>
        </div>
      </div>
      <div class="qb-kitq__input-row">
        <textarea class="qb-kitq__input" data-role="input" placeholder="Ask Kit-Q…" rows="2"></textarea>
        <button type="button" class="qb-kitq__send" data-role="send" title="Send (Enter)">
          ${iconMarkup("send", { size: "sm" })}
        </button>
      </div>
      <footer class="qb-kitq__foot">I'll show you items to confirm before they're added.</footer>
    </aside>
  `;

  const panel = host.querySelector<HTMLElement>("[data-role='kitq']")!;
  const backdrop = host.querySelector<HTMLElement>("[data-role='kitq-backdrop']")!;
  const msgsEl = host.querySelector<HTMLElement>("[data-role='msgs']")!;
  const inputEl = host.querySelector<HTMLTextAreaElement>("[data-role='input']")!;
  const sendBtn = host.querySelector<HTMLButtonElement>("[data-role='send']")!;

  function appendMsg(role: ChatMsg["role"], text: string, opts?: { thinking?: boolean }): HTMLElement {
    const el = document.createElement("div");
    el.className = `qb-kitq__msg qb-kitq__msg--${role}${opts?.thinking ? " is-thinking" : ""}`;
    el.textContent = text;
    msgsEl.appendChild(el);
    msgsEl.scrollTop = msgsEl.scrollHeight;
    return el;
  }

  async function send(): Promise<void> {
    const text = inputEl.value.trim();
    if (!text || thinking) return;
    inputEl.value = "";
    sendBtn.disabled = true;
    thinking = true;
    appendMsg("user", text);
    history.push({ role: "user", content: text });
    const thinkingEl = appendMsg("assistant", "Thinking…", { thinking: true });

    abortController?.abort();
    abortController = new AbortController();
    try {
      const res = await ctx.api<KitQResponse>("/qb-assistant", {
        method: "POST",
        body: JSON.stringify({
          message: text,
          history: history.slice(-14),
          quote: store.get(),
        }),
        signal: abortController.signal,
      });
      thinkingEl.remove();
      const reply = (res.reply || "").trim() || "(no reply)";
      appendMsg("assistant", reply);
      history.push({ role: "assistant", content: reply });
      const actions = res.actions ?? [];
      // add_items go through a preview the retailer confirms; everything else
      // (zones, markup, discounts, meta, updates, removes) applies immediately.
      const addActions = actions.filter((a): a is AddItemsAction => a.tool === "add_items");
      const otherActions = actions.filter((a) => a.tool !== "add_items");
      if (otherActions.length > 0) {
        applyAiActions(otherActions);
        const summary = summariseActions(otherActions);
        if (summary) {
          const tag = document.createElement("div");
          tag.className = "qb-kitq__action-tag";
          tag.textContent = summary;
          msgsEl.appendChild(tag);
          msgsEl.scrollTop = msgsEl.scrollHeight;
        }
      }
      if (addActions.length > 0) renderPreview(addActions);
    } catch (err) {
      thinkingEl.remove();
      if ((err as DOMException)?.name === "AbortError") return;
      const msg = err instanceof ApiError
        ? (err.status === 404 || err.status === 501
            ? "Kit-Q endpoint not available yet on this server."
            : `Kit-Q error (HTTP ${err.status})`)
        : "Kit-Q couldn't reach the server";
      appendMsg("assistant", msg);
      ctx.showToast(msg, "error");
    } finally {
      thinking = false;
      sendBtn.disabled = false;
    }
  }

  // Apply server-returned actions to the quote. Pricing-mutating actions
  // route through `recalcItem` to keep the pricing chain consistent.
  function applyAiActions(actions: KitQAction[]): void {
    store.set((d) => {
      for (const a of actions) {
        switch (a.tool) {
          case "add_items": {
            let zoneId: string | undefined;
            if (a.zoneName) {
              const wanted = a.zoneName.trim().toLowerCase();
              const found = d.zones.find((z) => z.name.toLowerCase() === wanted);
              if (found) zoneId = found.id;
              else {
                const nz = { id: newId("z"), name: a.zoneName, note: "", summary: "", items: [] };
                d.zones.push(nz);
                zoneId = nz.id;
              }
            }
            if (!zoneId) zoneId = d.zones[0]?.id;
            const zone = d.zones.find((z) => z.id === zoneId);
            if (!zone) continue;
            for (const partial of (a.items ?? [])) {
              const it: QuoteItem = {
                id: newId("i"),
                category: partial.category ?? "cabinets",
                supplier: partial.supplier ?? "TKC",
                code: partial.code ?? "",
                desc: partial.desc ?? "",
                qty: partial.qty ?? 1,
                width: partial.width ?? 0,
                height: partial.height ?? 0,
                depth: partial.depth ?? 0,
                notes: partial.notes ?? "",
                listPrice: partial.listPrice ?? 0,
                discPct: partial.discPct ?? 0,
                tradePrice: 0,
                markupMode: "pct",
                markupPct: store.get().defaults.markupPct ?? 0,
                markupGbp: 0,
                retailPrice: 0,
              };
              if (partial.doorRange) it.doorRange = partial.doorRange;
              if (partial.doorColour) it.doorColour = partial.doorColour;
              if (partial.carcassFinish) it.carcassFinish = partial.carcassFinish;
              if (partial.edging) it.edging = partial.edging;
              if (partial.hinging) it.hinging = partial.hinging;
              if (partial.material) it.material = partial.material;
              recalcItem(it, null);
              zone.items.push(it);
            }
            break;
          }
          case "update_items": {
            for (const upd of (a.updates ?? [])) {
              for (const z of d.zones) {
                const target = z.items.find((x) => x.id === upd.itemId);
                if (!target) continue;
                const numFields = new Set<keyof QuoteItem>(["qty", "width", "height", "depth", "listPrice", "discPct", "tradePrice", "markupPct", "markupGbp", "retailPrice"]);
                let pricingField: keyof QuoteItem | null = null;
                for (const [k, v] of Object.entries(upd)) {
                  if (k === "itemId") continue;
                  const key = k as keyof QuoteItem;
                  if (numFields.has(key)) {
                    (target as unknown as Record<string, number>)[k] = parseFloat(String(v)) || 0;
                    if (["listPrice", "discPct", "tradePrice", "markupPct", "markupGbp", "retailPrice"].includes(k)) {
                      pricingField = key;
                    }
                  } else {
                    (target as unknown as Record<string, unknown>)[k] = v;
                  }
                }
                if (pricingField) recalcItem(target, pricingField);
                break;
              }
            }
            break;
          }
          case "remove_items": {
            for (const id of (a.itemIds ?? [])) {
              for (const z of d.zones) z.items = z.items.filter((x) => x.id !== id);
            }
            break;
          }
          case "add_zone":
            d.zones.push({ id: newId("z"), name: a.name, note: a.note ?? "", summary: "", items: [] });
            break;
          case "duplicate_zone": {
            const src = d.zones.find((z) => z.name.toLowerCase() === a.sourceZoneName.toLowerCase());
            if (src) {
              d.zones.push({
                id: newId("z"),
                name: a.newName ?? `${src.name} (copy)`,
                note: src.note,
                summary: src.summary,
                items: src.items.map((it) => ({ ...it, id: newId("i") })),
              });
            }
            break;
          }
          case "set_supplier_discount":
            d.supplierDiscounts[a.supplier] = a.discPct;
            break;
          case "apply_markup": {
            const scope = a.scope ?? {};
            for (const z of d.zones) {
              if (scope.zoneName && z.name.toLowerCase() !== scope.zoneName.toLowerCase()) continue;
              for (const it of z.items) {
                if (scope.category && it.category !== scope.category) continue;
                if (scope.supplier && it.supplier !== scope.supplier) continue;
                if (a.mode === "pct") {
                  it.markupMode = "pct";
                  it.markupPct = a.value;
                  it.retailPrice = +calcRetailPct(it.tradePrice, a.value).toFixed(4);
                  it.markupGbp = +(it.tradePrice * a.value / 100).toFixed(2);
                } else {
                  it.markupMode = "gbp";
                  it.markupGbp = a.value;
                  it.retailPrice = +calcRetailGbp(it.tradePrice, a.value).toFixed(4);
                  it.markupPct = it.tradePrice > 0 ? +((a.value / it.tradePrice) * 100).toFixed(2) : 0;
                }
              }
            }
            break;
          }
          case "set_quote_meta": {
            const meta = a.meta ?? {};
            for (const [k, v] of Object.entries(meta)) {
              if (k === "vatRate" || k === "validDays") {
                (d as unknown as Record<string, number>)[k] = parseFloat(String(v)) || 0;
              } else {
                (d as unknown as Record<string, unknown>)[k] = v;
              }
            }
            break;
          }
        }
      }
    });
  }

  function summariseActions(actions: KitQAction[]): string {
    const parts: string[] = [];
    for (const a of actions) {
      switch (a.tool) {
        case "add_items": parts.push(`Added ${(a.items ?? []).length} item${(a.items ?? []).length === 1 ? "" : "s"}${a.zoneName ? ` to ${a.zoneName}` : ""}`); break;
        case "update_items": parts.push(`Updated ${(a.updates ?? []).length} item(s)`); break;
        case "remove_items": parts.push(`Removed ${(a.itemIds ?? []).length} item(s)`); break;
        case "add_zone": parts.push(`Added zone: ${a.name}`); break;
        case "duplicate_zone": parts.push(`Duplicated zone: ${a.sourceZoneName}`); break;
        case "set_supplier_discount": parts.push(`Set ${a.supplier} discount to ${a.discPct}%`); break;
        case "apply_markup": parts.push(`Applied ${a.value}${a.mode === "pct" ? "%" : "£"} markup`); break;
        case "set_quote_meta": parts.push("Updated quote details"); break;
      }
    }
    return parts.join(" · ");
  }

  // Build a QuoteItem from a model-proposed partial (mirrors the picker shape).
  function buildItem(partial: ItemPartial): QuoteItem {
    const it: QuoteItem = {
      id: newId("i"),
      category: partial.category ?? "cabinets",
      supplier: partial.supplier ?? "TKC",
      code: partial.code ?? "",
      desc: partial.desc ?? "",
      qty: partial.qty ?? 1,
      width: partial.width ?? 0,
      height: partial.height ?? 0,
      depth: partial.depth ?? 0,
      notes: partial.notes ?? "",
      listPrice: partial.listPrice ?? 0,
      discPct: partial.discPct ?? 0,
      tradePrice: 0,
      markupMode: "pct",
      markupPct: store.get().defaults.markupPct ?? 0,
      markupGbp: 0,
      retailPrice: 0,
    };
    if (partial.doorRange) it.doorRange = partial.doorRange;
    if (partial.doorColour) it.doorColour = partial.doorColour;
    if (partial.carcassFinish) it.carcassFinish = partial.carcassFinish;
    if (partial.edging) it.edging = partial.edging;
    if (partial.hinging) it.hinging = partial.hinging;
    if (partial.material) it.material = partial.material;
    return it;
  }

  // Apply confirmed add_items: resolve/create the zone, then push each item
  // through recalcItem + the shared pushItemToZone helper. Returns the count.
  function applyAddItems(addActions: AddItemsAction[]): number {
    let count = 0;
    for (const a of addActions) {
      let zoneId: string | undefined;
      store.set((d) => {
        if (a.zoneName) {
          const wanted = a.zoneName.trim().toLowerCase();
          const found = d.zones.find((z) => z.name.toLowerCase() === wanted);
          if (found) zoneId = found.id;
          else {
            const nz = { id: newId("z"), name: a.zoneName, note: "", summary: "", items: [] };
            d.zones.push(nz);
            zoneId = nz.id;
          }
        }
        if (!zoneId) zoneId = d.zones[0]?.id;
      });
      if (!zoneId) continue;
      for (const partial of (a.items ?? [])) {
        const it = buildItem(partial);
        recalcItem(it, null);
        pushItemToZone(store, zoneId, it);
        count++;
      }
    }
    return count;
  }

  // Render the preview card for proposed items, with Confirm / Discard.
  function renderPreview(addActions: AddItemsAction[]): void {
    const flat = addActions.flatMap((a) => (a.items ?? []));
    if (!flat.length) return;
    const rows = flat.map((it) => {
      const qty = it.qty ?? 1;
      const size = it.width ? ` · ${it.width}mm` : "";
      const price = it.listPrice ? `£${Number(it.listPrice).toLocaleString("en-GB")}` : "—";
      return `<div class="qb-kitq__preview-item"><span>${qty}× ${escapeHtml(it.desc ?? it.code ?? "item")}${size}</span><span>${price}</span></div>`;
    }).join("");

    const card = document.createElement("div");
    card.className = "qb-kitq__preview";
    card.innerHTML = `
      <div class="qb-kitq__preview-title">Preview — ${flat.length} item${flat.length === 1 ? "" : "s"} to add</div>
      ${rows}
      <div class="qb-kitq__preview-actions">
        <button type="button" class="qb-kitq__confirm" data-role="confirm">Confirm &amp; add</button>
        <button type="button" class="qb-kitq__discard" data-role="discard">Discard</button>
      </div>`;
    msgsEl.appendChild(card);
    msgsEl.scrollTop = msgsEl.scrollHeight;

    const finish = (note: string, cls: string): void => {
      card.classList.add("is-done");
      card.querySelector(".qb-kitq__preview-actions")?.remove();
      const tag = document.createElement("div");
      tag.className = cls;
      tag.textContent = note;
      msgsEl.appendChild(tag);
      msgsEl.scrollTop = msgsEl.scrollHeight;
    };
    card.querySelector("[data-role='confirm']")!.addEventListener("click", () => {
      const n = applyAddItems(addActions);
      finish(`Added ${n} item${n === 1 ? "" : "s"} to the quote.`, "qb-kitq__action-tag");
    }, { once: true });
    card.querySelector("[data-role='discard']")!.addEventListener("click", () => {
      finish("Discarded — nothing added.", "qb-kitq__msg qb-kitq__msg--system");
    }, { once: true });
  }

  // Wire UI ────────────────────────────────────────────────────────────────
  function closePanel(): void {
    panel.classList.remove("is-open");
    backdrop.classList.remove("is-open");
  }
  panel.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", closePanel);
  backdrop.addEventListener("click", closePanel);
  sendBtn.addEventListener("click", () => void send());
  inputEl.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  });

  return {
    open() {
      panel.classList.add("is-open");
      backdrop.classList.add("is-open");
      window.setTimeout(() => inputEl.focus(), 80);
    },
    close() {
      closePanel();
    },
    destroy() {
      abortController?.abort();
      backdrop.remove();
      panel.remove();
    },
  };
}
